/**
 * Tandem console — "Settings". Working console configuration only; Engine policy
 * lives on the separate hideable Engine settings page.
 */
import { Box, Typography } from "@mui/material";
import { T } from "./tokens";
import { Panel } from "./ui";

// Pages that can be hidden from the sidebar. Overview, Schedule, Reports,
// Consultants and Settings always show.
const HIDEABLE_PAGES: Array<{ key: string; label: string; desc: string }> = [
    { key: "engine", label: "Engine settings", desc: "Phase 2 — auto-booking policy, not connected yet" },
];

const SettingsScreen = ({
    hiddenPages,
    onTogglePage,
    showPoints = true,
    onToggleShowPoints,
}: {
    hiddenPages?: Set<string>;
    onTogglePage?: (key: string) => void;
    showPoints?: boolean;
    onToggleShowPoints?: (on: boolean) => void;
}) => (
    <Box sx={{ padding: "24px 28px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))", gap: "18px", alignItems: "start" }}>
        <Panel title="Console pages" sub="Show or hide pages in the sidebar">
            <Box sx={{ display: "flex", flexDirection: "column" }}>
                {HIDEABLE_PAGES.map((p) => {
                    const visible = !hiddenPages?.has(p.key);
                    return (
                        <Box key={p.key} sx={{ display: "flex", alignItems: "center", gap: "10px", padding: "9px 0", borderTop: `1px solid ${T.borderSoft}` }}>
                            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                                <Typography sx={{ fontSize: "13px", fontWeight: 700, color: T.ink2 }}>{p.label}</Typography>
                                <Typography sx={{ fontSize: "11px", color: T.muted }}>{p.desc}</Typography>
                            </Box>
                            <Box
                                component="button"
                                onClick={() => onTogglePage?.(p.key)}
                                sx={{
                                    border: "none",
                                    borderRadius: "999px",
                                    padding: "4px 12px",
                                    fontSize: "11px",
                                    fontWeight: 800,
                                    fontFamily: T.font,
                                    cursor: "pointer",
                                    background: visible ? T.greenTint : "#F1F3F9",
                                    color: visible ? T.green : T.muted,
                                }}
                            >
                                {visible ? "Visible" : "Hidden"}
                            </Box>
                        </Box>
                    );
                })}
            </Box>
            <Typography sx={{ fontSize: "11px", color: T.muted2, mt: "12px" }}>
                Overview, Schedule, Reports, Consultants and Settings always show.
            </Typography>
        </Panel>

        <Panel title="Data" sub="Where this console reads from">
            <Typography sx={{ fontSize: "13px", color: T.ink2, lineHeight: 1.6 }}>
                Tandem reads live data from the WorkNest Tools API — visits, consultant diaries and the
                current automation window. No sample data is used anywhere. Some figures are
                nonetheless <b>derived</b> rather than measured — pairing candidates and the month
                utilisation are calculated in the browser from booked windows, and are labelled as
                such where they appear.
            </Typography>
            <Typography sx={{ fontSize: "12.5px", color: T.muted, mt: "10px", lineHeight: 1.6 }}>
                Org-wide views (Schedule, Reports, Consultants) come from the weekly visit summary, which
                the API returns only to users who are both flagged <b>H&amp;S Admin</b> in Salesforce and
                hold a recognised H&amp;S consultant job title.
            </Typography>
        </Panel>

        <Panel title="Points model" sub="Scoring rules, industry rates and targets">
            <Box sx={{ display: "flex", alignItems: "center", gap: "10px", padding: "9px 0", borderTop: `1px solid ${T.borderSoft}` }}>
                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                    <Typography sx={{ fontSize: "13px", fontWeight: 700, color: T.ink2 }}>Show points</Typography>
                    <Typography sx={{ fontSize: "11px", color: T.muted }}>
                        Off hides every points figure across the console and the CSV, and measures
                        consultants on visits instead
                    </Typography>
                </Box>
                <Box
                    component="button"
                    onClick={() => onToggleShowPoints?.(!showPoints)}
                    aria-pressed={showPoints}
                    sx={{
                        border: "none",
                        borderRadius: "999px",
                        padding: "4px 12px",
                        fontSize: "11px",
                        fontWeight: 800,
                        fontFamily: T.font,
                        cursor: "pointer",
                        background: showPoints ? T.greenTint : "#F1F3F9",
                        color: showPoints ? T.green : T.muted,
                    }}
                >
                    {showPoints ? "Shown" : "Hidden"}
                </Box>
            </Box>
            <Typography sx={{ fontSize: "11px", color: T.muted2, mt: "12px", lineHeight: 1.6 }}>
                This setting is remembered in this browser only — it does not change what anyone else
                sees. The scores are still calculated either way; turning this off only stops them
                being shown.
            </Typography>
        </Panel>
    </Box>
);

export default SettingsScreen;
